import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook to detect barcode scanner input
 * Barcode scanners typically send characters very fast and end with Enter
 * This hook differentiates scanner input from normal keyboard typing
 */
export function useBarcodeScanner(options = {}) {
  const {
    onScan,
    onError,
    minLength = 3,        // Minimum barcode length
    maxDelay = 50,        // Max ms between characters (scanners are fast)
    endChar = 'Enter',    // Character that ends the scan
    enabled = true,       // Enable/disable the scanner
    preventDefault = true // Prevent default behavior when scanning
  } = options;

  const [isScanning, setIsScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  
  const bufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  const timeoutRef = useRef(null);

  const resetBuffer = useCallback(() => {
    bufferRef.current = '';
    setIsScanning(false);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;

    const currentTime = Date.now();
    const timeSinceLastKey = currentTime - lastKeyTimeRef.current;
    lastKeyTimeRef.current = currentTime;

    // Clear buffer if too much time has passed (user is typing normally)
    if (timeSinceLastKey > maxDelay && bufferRef.current.length > 0) {
      resetBuffer();
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // End of scan
    if (event.key === endChar) {
      const scannedCode = bufferRef.current.trim();
      
      if (scannedCode.length >= minLength) {
        // Valid scan detected
        if (preventDefault) {
          event.preventDefault();
        }
        
        setLastScan({
          code: scannedCode,
          timestamp: currentTime
        });
        
        if (onScan) {
          onScan(scannedCode);
        }
      }
      
      resetBuffer();
      return;
    }

    // Only accept printable characters
    if (event.key.length === 1) {
      // Detect if this is scanner input (very fast typing)
      if (bufferRef.current.length === 0 || timeSinceLastKey <= maxDelay) {
        bufferRef.current += event.key;
        setIsScanning(true);
        
        // Set timeout to reset buffer if no more input
        timeoutRef.current = setTimeout(() => {
          if (bufferRef.current.length > 0 && bufferRef.current.length < minLength) {
            resetBuffer();
          }
        }, maxDelay * 3);
      } else {
        // Normal typing, reset
        resetBuffer();
      }
    }
  }, [enabled, maxDelay, minLength, endChar, preventDefault, onScan, resetBuffer]);

  useEffect(() => {
    if (enabled) {
      window.addEventListener('keydown', handleKeyDown, true);
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, handleKeyDown]);

  return {
    isScanning,
    lastScan,
    resetBuffer
  };
}

export default useBarcodeScanner;
